// Shared builders + detectors for the SC-2157 confirmation round-trip user
// messages. The agent needs the finding_id in the message body (BE
// disambiguator when multiple confirmations are pending), but the FE hides
// these synthetic bubbles so a confirm click doesn't echo as a redundant
// user message.

export function buildConfirmMessage(findingId: string): string {
  return `Yes, apply finding ${findingId} as proposed.`;
}

export function buildCancelMessage(findingId: string): string {
  return `Cancel — don't apply finding ${findingId}.`;
}

// Charset matches MongoDB ObjectId (lowercase hex), arbitrary alphanumeric ids,
// and UUID-style ids with hyphens — keeps the detector in sync with whatever
// shape `proposed_action.finding_id` arrives as. Per Greptile P2 / SC-2168.
const CONFIRM_PATTERN = /^Yes, apply finding [0-9a-zA-Z-]+ as proposed\.$/;
const CANCEL_PATTERN = /^Cancel — don't apply finding [0-9a-zA-Z-]+\.$/;

/**
 * Whether a user-message string is one of the canonical confirm/cancel
 * payloads emitted by ConfirmationFooter. Anchored regex — only exact matches
 * count, so a user manually typing extra prose around the same shape still
 * renders normally.
 */
export function isConfirmationUserMessage(text: string): boolean {
  return CONFIRM_PATTERN.test(text) || CANCEL_PATTERN.test(text);
}

/**
 * Scope sentence for an apply_finding confirmation card (SC-2540), derived from
 * the BE `proposed_action`. A non-empty `entity_ids` list is a SCOPED apply (one
 * or a few named rows); its absence is the WHOLE finding. `affected_count`
 * (SC-2750) supplies the whole-finding total — when it's absent (pre-SC-2750
 * backend) we fall back to a number-free phrasing rather than fabricate a count.
 *
 * A plain sentence, no leading glyph: the card renders it as information under
 * the title. A glyph-led line on its own row above Confirm read as a control
 * someone had to click to pick the scope (SC-3516).
 */
export function applyScopeLabel(
  entityIds: string[] | undefined,
  affectedCount: number | undefined,
): string {
  // Defensive dedupe: apply is idempotent per entity, so a repeated SKU affects
  // one row — and a pre-SC-2750 backend echoes the raw requested list. Keep the
  // count honest regardless of which backend is live.
  const unique = entityIds ? Array.from(new Set(entityIds)) : undefined;
  if (unique && unique.length > 0) {
    return unique.length === 1
      ? `Applies to this one entity — ${unique[0]}.`
      : `Applies to these ${unique.length} entities.`;
  }
  // Whole finding. Only cite a number when the BE gave us a real positive count;
  // otherwise stay number-free (don't invent an affected total on the FE).
  if (affectedCount !== undefined && affectedCount > 0) {
    const noun = affectedCount === 1 ? 'entity' : 'entities';
    return `Applies to the whole finding — all ${affectedCount} ${noun}.`;
  }
  return 'Applies to the whole finding — all affected rows.';
}

/**
 * The confirm-card title without doubled end punctuation. The BE builds it as
 * `Apply finding: {summary}?`, so a summary that already ends in a sentence mark
 * reads "…state it.?" — collapse any run of `.`/`!`/`?`/`…` before the closing
 * `?` into the `?` alone. Display-only; anything not ending in that shape is
 * returned unchanged.
 */
export function tidyConfirmTitle(summary: string): string {
  return summary.replace(/\s*[.!?…]+\s*\?\s*$/, '?');
}
